<template>
  <spinner v-if="loading" />
  <div v-else-if="stage === 1" class="container-positions">
    <div v-for="(spreadGroup, aIx) in groupedSpreadsHome" :key="`home-${aIx}`">
      <div v-if="spreadGroup.home" class="box-position">
        <!-- Title of box (mySide) -->
        <my-position-header
          :game="game"
          :obj-side="spreadGroup"
          :type-bet="'spread'"
          :key-obj="aIx"
          :home-away="'home'"
          @change-accordeon-opened="changeAccordeonOpened"
        />
        <div v-if="spreadGroup.expanded">
          <div v-if="spreadGroup.home">
            <my-position-accordeon
              v-if="spreadGroup.home.bets.length > 1"
              :obj-side="spreadGroup.home"
              :league="league"
              @display-bet="displayBet"
            />
            <my-position-accordeon-item
              v-else
              :obj-side="spreadGroup.home"
              :bet="spreadGroup.home.bets[0]"
              :league="league"
              @display-bet="displayBet"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-for="(spreadGroup, aIx) in groupedSpreadsAway" :key="`away-${aIx}`">
      <div
        v-if="spreadGroup.away && spreadGroup.away.bets.length > 0"
        class="box-position"
      >
        <!-- Title of box (mySide) -->
        <my-position-header
          :game="game"
          :obj-side="spreadGroup"
          :type-bet="'spread'"
          :key-obj="aIx"
          :home-away="'away'"
          @change-accordeon-opened="changeAccordeonOpened"
        />
        <div v-if="spreadGroup.expanded">
          <div v-if="spreadGroup.away">
            <my-position-accordeon
              v-if="spreadGroup.away.bets.length > 1"
              :obj-side="spreadGroup.away"
              :league="league"
              @display-bet="displayBet"
            />
            <my-position-accordeon-item
              v-else
              :obj-side="spreadGroup.away"
              :bet="spreadGroup.away.bets[0]"
              :league="league"
              @display-bet="displayBet"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="stage === 2">
    <button class="button-back mb-1" @click="stage = 1">
      ⇠ Return To Summary
    </button>
    <double-bet-info-modal :bet="session" />
  </div>
</template>
<script setup>
//CORE
import { ref, computed, onMounted } from 'vue';
import {
  calculateWin,
  getSideString,
  signOdds,
  fixed2,
  calculateAverageOdds,
  getCommissionPct,
} from '@/core/utils';
import {
  commissionAdjustedRisk,
  commissionAdjustedWin,
} from '@/core/commissionUtils';
import { useUserStore } from '@/stores/user';
import { useMiscStore } from '@/stores/store';
import { emitter } from '@/core/emitter';

//COMPONENTS
import MyPositionAccordeon from '@/components/userBets/MyPositionAccordeon.vue';
import MyPositionAccordeonItem from '@/components/userBets/MyPositionSingleBet.vue';
import MyPositionHeader from '@/components/userBets/myPosition/MyPositionHeader.vue';
import Spinner from '@/components/global/Spinner.vue';
import DoubleBetInfoModal from '@/components/userBets/modals/DoubleBetInfoModal.vue';

//PROPS
//PROPS
const props = defineProps({
  game: {
    type: Object,
    default: null,
  },
  spreadAway: {
    type: Object,
    required: true,
  },
  spreadHome: {
    type: Object,
    required: true,
  },
  spreadRisk: {
    type: Object,
    required: true,
  },
  avgOddsSpreads: {
    type: Number,
    required: true,
  },
  avgOddsMoneyLine: {
    type: Number,
    required: true,
  },
  groupedBets: {
    type: Object,
    required: true,
  },
  league: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close-modal']);

const userStore = useUserStore();

// DATA
const session = ref({});
const away = ref([]);
const home = ref([]);
const stage = ref(1);
const dataAway = ref(false);
const dataHome = ref(false);
const loading = ref(true);
const spreadNameAway = ref('');
const spreadNameHome = ref('');
const mySpreadAway = ref('');
const mySpreadHome = ref('');
const awayRiskWin = ref([]);
const homeRiskWin = ref([]);
const homeOpened = ref(false);
const awayOpened = ref(false);
const groupedSpreadsAway = ref(null);
const groupedSpreadsHome = ref(null);

// Computed
const isPositive = computed(() => {
  return session.value.hedgeNet + session.value.bet > 0;
});

const isNegative = computed(() => {
  return session.value.hedgeNet + session.value.bet < 0;
});

const defaultRotationNumbers = computed(() => {
  return userStore.user.defaultRotationNumbers;
});

const homeOnlyBets = computed(() => {
  const obj = home.value[0];
  delete obj.sumRisk;
  delete obj.sumWin;
  return obj;
});

const awayOnlyBets = computed(() => {
  const obj = away.value[0];
  delete obj.sumRisk;
  delete obj.sumWin;
  return obj;
});

const computedCommissionRate = computed(() => {
  if (!userStore.user.viewOddsWithCommission) return null;
  return getCommissionPct(
    userStore.user.commissionCharged,
    props.league,
    'spread',
    userStore.user.commissionDiscounts,
  );
});

const bestSide = computed(() => {
  if (
    !homeRiskWin.value.sumRisk ||
    awayRiskWin.value.sumRisk > homeRiskWin.value.sumRisk
  ) {
    return {
      spreadName: spreadNameAway.value,
      avgOdds: fixed2(awayRiskWin.value.avgOdds),
      risk: fixed2(awayRiskWin.value.sumRisk),
      win: fixed2(awayRiskWin.value.sumWin),
      side: 'away',
    };
  }

  return {
    spreadName: spreadNameHome.value,
    avgOdds: fixed2(homeRiskWin.value.avgOdds),
    risk: fixed2(homeRiskWin.value.sumRisk),
    win: fixed2(homeRiskWin.value.sumWin),
    side: 'home',
  };
});

const gameObj = computed(() => {
  return bestSide.value.side === 'home'
    ? homeOnlyBets.value[mySpreadHome.value[0]][0].game
    : awayOnlyBets.value[mySpreadAway.value[0]][0].game;
});

const groupedHome = computed(() => {
  const groupedHomeObj = {};
  const homeOnlyBetsValue = homeOnlyBets.value;
  const homeSpreadKeys = Object.keys(homeOnlyBetsValue);

  homeSpreadKeys.forEach((s) => {
    if (!groupedHomeObj[s]) {
      groupedHomeObj[s] = {
        home: false,
        away: false,
        mySide: false,
        expanded: true,
      };
    }

    if (homeOnlyBetsValue[s]) {
      const objHome = {
        bets: [],
        realSpread: s,
      };
      groupedHomeObj[s].home = objHome;
      if (awayOnlyBets.value[-s]) {
        const homeBets = homeOnlyBetsValue[s].map((obj) => ({
          ...obj,
          side: 'home',
        }));
        const awayBets = awayOnlyBets.value[-s].map((obj) => ({
          ...obj,
          side: 'away',
        }));
        groupedHomeObj[s].home.bets = [...homeBets, ...awayBets];
      } else {
        const homeBets = homeOnlyBetsValue[s].map((obj) => ({
          ...obj,
          side: 'home',
        }));
        groupedHomeObj[s].home.bets = [...homeBets];
      }
    }
  });

  Object.keys(groupedHomeObj).forEach((k) => {
    const bets = groupedHomeObj[k].home.bets;
    const realSpread = groupedHomeObj[k].home.realSpread;
    const { sumRiskBets, sumWinBets, participantInfo, gameObjValue } =
      reduceRiskWinByBet(bets);
    groupedHomeObj[k].home = {
      bets,
      realSpread,
      sumRiskBets,
      sumWinBets,
      participantInfo,
      gameObj: gameObjValue,
      avgOdds: calculateAvgOddsSpreads(sumRiskBets, sumWinBets),
    };

    const homeBets = bets.filter((bet) => bet.side === 'home');
    const {
      sumRiskBets: homeSumRiskBets,
      sumWinBets: homeSumWinBets,
      participantInfo: participantInfoHome,
    } = reduceRiskWinByBet(homeBets);
    const getBestSideBetHomeObj = {
      ...groupedHomeObj[k].home,
      bets: homeBets,
      sumRiskBets: homeSumRiskBets,
      sumWinBets: homeSumWinBets,
      participantInfo: participantInfoHome,
    };

    const awayBets = bets.filter((bet) => bet.side === 'away');
    const {
      sumRiskBets: awaySumRiskBets,
      sumWinBets: awaySumWinBets,
      participantInfo: participantInfoAway,
    } = reduceRiskWinByBet(awayBets);
    const getBestSideBetAwayObj =
      groupedAway.value[-k] && awayBets.length
        ? {
            ...groupedAway.value[-k].away,
            bets: awayBets,
            sumRiskBets: awaySumRiskBets,
            sumWinBets: awaySumWinBets,
            participantInfo: participantInfoAway,
          }
        : null;

    groupedHomeObj[k].mySide = getBestSideBet(
      getBestSideBetHomeObj,
      getBestSideBetAwayObj,
    );
    if (getBestSideBetAwayObj) groupedHomeObj[k].away = getBestSideBetAwayObj;
    groupedHomeObj[k].home.participantInfo =
      getBestSideBetHomeObj.participantInfo;
    groupedHomeObj[k].home.sumRiskBets = getBestSideBetHomeObj.sumRiskBets;
    groupedHomeObj[k].home.sumWinBets = getBestSideBetHomeObj.sumWinBets;
  });

  return groupedHomeObj;
});

const groupedAway = computed(() => {
  const groupedAwayObj = {};
  const awayOnlyBetsValue = awayOnlyBets.value;
  const awaySpreadKeys = Object.keys(awayOnlyBetsValue);

  awaySpreadKeys.forEach((s) => {
    if (!groupedAwayObj[s]) {
      groupedAwayObj[s] = {
        home: false,
        away: false,
        mySide: false,
        expanded: true,
      };
    }

    if (awayOnlyBetsValue[s]) {
      const objAway = {
        bets: [],
        realSpread: s,
      };
      groupedAwayObj[s].away = objAway;
      if (!homeOnlyBets.value[-s]) {
        const awayBets = awayOnlyBetsValue[s].map((obj) => ({
          ...obj,
          side: 'away',
        }));
        groupedAwayObj[s].away.bets = [...awayBets];
      }
    }
  });

  Object.keys(groupedAwayObj).forEach((k) => {
    const bets = groupedAwayObj[k].away.bets;
    if (bets.length > 0) {
      const realSpread = groupedAwayObj[k].away.realSpread;
      const { sumRiskBets, sumWinBets, participantInfo, gameObjValue } =
        reduceRiskWinByBet(bets);
      groupedAwayObj[k].away = {
        bets,
        realSpread,
        sumRiskBets,
        sumWinBets,
        participantInfo,
        gameObj: gameObjValue,
        avgOdds: calculateAvgOddsSpreads(sumRiskBets, sumWinBets),
      };
      groupedAwayObj[k].mySide = getBestSideBet(null, groupedAwayObj[k].away);
    }
  });

  return groupedAwayObj;
});

onMounted(() => {
  away.value = [Object.assign({}, props.spreadAway)];
  away.value = Object.assign([], away.value);
  home.value = [Object.assign({}, props.spreadHome)];
  home.value = Object.assign([], home.value);
  mySpreadAway.value = Object.keys(away.value[0]);
  mySpreadHome.value = Object.keys(home.value[0]);

  if (mySpreadAway.value[0]) {
    spreadNameAway.value =
      away.value[Object.keys(away.value)][
        mySpreadAway.value[0]
      ][0].game.participants[0].longName;
    awayRiskWin.value =
      away.value[Object.keys(away.value)][mySpreadAway.value[0]].avgOddsSum;
    dataAway.value = true;
  }

  if (mySpreadHome.value[0]) {
    spreadNameHome.value =
      home.value[Object.keys(home.value)][
        mySpreadHome.value[0]
      ][0].game.participants[1].longName;
    homeRiskWin.value =
      home.value[Object.keys(home.value)][mySpreadHome.value[0]].avgOddsSum;
    dataHome.value = true;
  }

  loading.value = false;
  groupBySpread();
});

// METHODS

function close() {
  emit('close-modal');
}

function displayBet(spread) {
  session.value = spread;
  emitter.emit('displayBet', session.value);
  loading.value = false;
  stage.value = 2;
}

function groupBySpread() {
  const groupedHomeValue = groupedHome.value;
  const groupedAwayValue = groupedAway.value;
  groupedSpreadsAway.value = groupedAwayValue;
  groupedSpreadsHome.value = groupedHomeValue;
}

function reduceRiskWinByBet(bets) {
  let sumRiskBets = 0;
  let sumWinBets = 0;
  let participantInfo;
  let gameObj;

  if (userStore.user.viewOddsWithCommission) {
    bets.forEach((bet) => {
      const betRisk =
        bet.origin === 'offer'
          ? bet.bet
          : commissionAdjustedRisk(
              bet.bet,
              bet.odds,
              bet.game.league.toLowerCase(),
              'spread',
              userStore,
            );
      const tempWin = calculateWin(bet.bet, bet.odds);
      const betWin =
        bet.origin === 'offer'
          ? tempWin
          : commissionAdjustedWin(
              tempWin,
              bet.game.league.toLowerCase(),
              'spread',
              userStore,
            );
      sumRiskBets += betRisk;
      sumWinBets += betWin;
      participantInfo = bet.game.participants.find(
        (p) => p.id === bet.participantID,
      );
      gameObj = bet.game;
    });
  } else {
    bets.forEach((bet) => {
      sumRiskBets += bet.bet;
      sumWinBets += calculateWin(bet.bet, bet.odds);
      participantInfo = bet.game.participants.find(
        (p) => p.id === bet.participantID,
      );
      gameObj = bet.game;
    });
  }

  return {
    sumRiskBets: fixed2(sumRiskBets),
    sumWinBets: fixed2(sumWinBets),
    participantInfo,
    gameObj,
  };
}

function getBestSideBet(homeObj, awayObj) {
  const accumHomeRisk =
    homeObj && homeObj.sumRiskBets !== undefined
      ? parseFloat(homeObj.sumRiskBets)
      : 0;
  const accumHomeWin =
    homeObj && homeObj.sumWinBets !== undefined
      ? parseFloat(homeObj.sumWinBets)
      : 0;
  const accumAwayRisk =
    awayObj && awayObj.sumRiskBets !== undefined
      ? parseFloat(awayObj.sumRiskBets)
      : 0;
  const accumAwayWin =
    awayObj && awayObj.sumWinBets !== undefined
      ? parseFloat(awayObj.sumWinBets)
      : 0;
  const winAwayMinusRiskHome = accumAwayWin - accumHomeRisk;
  const winHomeMinusRiskAway = accumHomeWin - accumAwayRisk;
  const objBigWin =
    winHomeMinusRiskAway > winAwayMinusRiskHome ? homeObj : awayObj;
  let calculatedRisk;
  let calculatedWin;
  if (
    (accumHomeRisk === 0 && accumHomeWin === 0) ||
    (accumAwayRisk === 0 && accumAwayWin === 0)
  ) {
    calculatedRisk = accumHomeRisk !== 0 ? accumHomeRisk : accumAwayRisk;
    calculatedWin = accumHomeWin !== 0 ? accumHomeWin : accumAwayWin;
  } else if (accumAwayWin - accumHomeRisk > accumHomeWin - accumAwayRisk) {
    calculatedRisk = accumAwayRisk - accumHomeWin;
    calculatedWin = accumAwayWin - accumHomeRisk;
  } else if (accumHomeWin - accumAwayRisk > accumAwayWin - accumHomeRisk) {
    calculatedRisk = accumHomeRisk - accumAwayWin;
    calculatedWin = accumHomeWin - accumAwayRisk;
  } else {
    calculatedRisk = 0;
    calculatedWin = 0;
  }
  const risk = fixed2(calculatedRisk);
  const win = fixed2(calculatedWin);
  const odds = calculateAvgOddsSpreads(calculatedRisk, calculatedWin);
  return {
    spreadName: objBigWin.participantInfo.longName,
    spread: objBigWin.realSpread,
    odds,
    risk,
    win,
  };
}

function calculateAvgOddsSpreads(risk, win) {
  const avgOdds = calculateAverageOdds(risk, win);
  const avgOddsABS =
    Number.isNaN(avgOdds) || Math.abs(avgOdds) === Infinity ? 0 : avgOdds;
  return avgOddsABS > -100 && avgOddsABS < 100 ? 0 : fixed2(avgOddsABS);
}

function changeAccordeonOpened({ keyObj, homeAway }) {
  if (homeAway === 'home') {
    groupedSpreadsHome.value[keyObj].expanded =
      !groupedSpreadsHome.value[keyObj].expanded;
  } else {
    groupedSpreadsAway.value[keyObj].expanded =
      !groupedSpreadsAway.value[keyObj].expanded;
  }
}
</script>

<style scoped lang="scss">
@use '@/scss/tokens.scss' as main;

.container-positions {
  padding-left: 20px;
  padding-right: 20px;
}
.box-position {
  border-radius: 8px;
  background: main.$white_01;
  margin: 0;
  margin-left: -20px;
  margin-right: -20px;
  margin-bottom: 16px;
}
.title-position {
  background: main.$blue-702;
  color: main.$white;
  font-size: small;
  font-weight: bold;
  padding: 8px;
  margin: 0;
  border-top-left-radius: 7px;
  border-top-right-radius: 7px;
  cursor: pointer;
}
.button-back {
  width: 100%;
  background: main.$grey-500;
  color: main.$blue-1100;
  font-weight: bold;
}
</style>
